import { create } from 'zustand'
import {
  createWavePlanExport,
  getWavePlanExport,
  wavePlanExportPrefix,
} from '@/api/wavePlans'
import { toast } from '@/stores/useToastStore'
import type { ExportRead } from '@/types'
import { downloadFileFromUrl } from '@/utils/downloadFile'
import { getErrorMessage } from '@/utils/getErrorMessage'

const POLL_MS = 2000
const MAX_FAILED_POLLS = 5
// The API client waits forever by default, including for a login refresh.
const REQUEST_LIMIT_MS = 60 * 1000
// Workers can be busy with other jobs, or be on their way up during a deployment.
const START_LIMIT_MS = 5 * 60 * 1000
// Far longer than the largest plan's workbook takes.
const TOTAL_LIMIT_MS = 15 * 60 * 1000
const DID_NOT_FINISH = 'The export did not finish. Please try again.'

// Kept outside React: each wave planning page is mounted anew, and an export has to reach its
// download wherever the person is by then.
const useRunning = create<{ running: ReadonlySet<string> }>(() => ({
  running: new Set<string>(),
}))

function setRunning(id: string, running: boolean) {
  useRunning.setState(state => {
    const next = new Set(state.running)
    if (running) next.add(id)
    else next.delete(id)
    return { running: next }
  })
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function withinLimit<T>(request: Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(DID_NOT_FINISH)),
      REQUEST_LIMIT_MS
    )
    request.then(resolve, reject).finally(() => clearTimeout(timer))
  })
}

/** Waits for the job to finish. Fails if it never starts, so nothing spins forever. */
async function finished(id: string, exportId: string): Promise<ExportRead> {
  const started = Date.now()
  let failedPolls = 0
  for (;;) {
    await wait(POLL_MS)
    let current: ExportRead
    try {
      current = await withinLimit(getWavePlanExport(id, exportId))
      failedPolls = 0
    } catch (error) {
      failedPolls += 1
      if (failedPolls >= MAX_FAILED_POLLS) throw error
      continue
    }
    if (current.status === 'COMPLETED') return current
    if (current.status === 'FAILED') {
      throw new Error(current.error_message ?? 'Export failed')
    }
    const waited = Date.now() - started
    if (
      waited > TOTAL_LIMIT_MS ||
      (current.status === 'PENDING' && waited > START_LIMIT_MS)
    ) {
      throw new Error(DID_NOT_FINISH)
    }
  }
}

/** Builds the plan's workbook in the background and downloads it. One at a time per plan. */
export async function exportWavePlan(
  id: string,
  apName: string | null | undefined
): Promise<void> {
  if (useRunning.getState().running.has(id)) return
  setRunning(id, true)
  try {
    const job = await withinLimit(createWavePlanExport(id))
    const done = await finished(id, job.id)
    if (!done.download_url) throw new Error('No download URL available')
    const date = new Date().toISOString().split('T')[0]
    downloadFileFromUrl(
      done.download_url,
      `${wavePlanExportPrefix(apName)}-${date}.xlsx`
    )
    toast.success('Export downloaded successfully')
  } catch (error) {
    toast.error(getErrorMessage(error, 'Could not export the wave plan'))
  } finally {
    setRunning(id, false)
  }
}

export function useIsExportingWavePlan(id: string): boolean {
  return useRunning(state => state.running.has(id))
}
